import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SiteCombobox } from "@/components/SiteCombobox";
import {
  SupportAgentChat,
  type SupportAgentHandoff,
} from "@/components/support-agent/SupportAgentChat";
import { Feature, Permission, useSession } from "@/components/session-provider";
import { formatPhone, normalisePhone } from "@/lib/phone";
import { writeRequestDraft } from "@/lib/request-draft";
import { createTicket, getReference, type Reference } from "@/lib/tickets";

type Phase = "gate" | "chat" | "created";

/**
 * The top bar's OTHER way into a new request — describe the problem to TARS first, from
 * anywhere in the app, rather than picking a team and a category before it will listen.
 *
 * ⚠️ CATEGORYLESS BY DESIGN, and nothing about `SupportAgentChat` or the endpoint behind it
 * changes for that. `POST /api/assistant/chat` already sends every active, AI-eligible
 * category name to the model and matches its answer back to a real category server-side
 * (`AssistantEndpoints.cs`) — the wizard has simply never exercised that path with no
 * category chosen first. Team follows for free: a category belongs to exactly one team
 * (D-71), so once a category is matched, so is the team it's on.
 *
 * ⚠️ SELF-SERVICE ONLY. No "raise for somebody else" picker — every top-company chat
 * launcher this was modelled on (Zendesk, Intercom, ServiceNow's Virtual Agent) is about the
 * person talking to it, and on-behalf-of needs a directory lookup before a conversation could
 * even start (D-51's own fence on that picker). Raising for a colleague still goes through
 * the ordinary wizard.
 *
 * ⚠️ THE CONTACT GATE ASKS ONLY WHAT THE PROFILE DOES NOT ALREADY ANSWER — the same
 * `contactGateDone` idea `new.tsx` already uses, reading the same `GET /api/me` fields
 * (`user.contact.contactPhone`, `user.siteId`). Reuses the phone/site validation and the
 * `SiteCombobox` the wizard uses; does not reuse the wizard's own contact-gate MARKUP, which
 * is entangled with attachments, participants and the on-behalf-of picker this dialog
 * deliberately does not offer.
 *
 * ⚠️ A FRESH CONVERSATION EVERY OPEN. Nothing here is persisted beyond the browser tab, and
 * closing the dialog forgets it — the same transient-until-a-button-presses-Create shape
 * D-160 already established for the wizard's own chat. `SupportAgentChat` is remounted (via
 * `key`) on every open rather than resumed, so a stale reply from a previous conversation can
 * never bleed into a new one.
 *
 * ⚠️ WHAT THIS CANNOT DO THAT THE WIZARD CAN: attachments, participants, or naming an
 * assignee at intake. All three are add-a-file/invite-somebody/hand-it-to-someone actions
 * that work perfectly well on the ticket AFTER it exists — this dialog exists to get a
 * request filed in under a minute, not to replace the wizard.
 */
export function AssistantLauncher() {
  const { user, can, hasFeature, refresh } = useSession();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [reference, setReference] = useState<Reference | null>(null);
  const [referenceError, setReferenceError] = useState(false);

  const eligibleForFeatureAndPermission =
    hasFeature(Feature.AiAssistant) && can(Permission.TicketCreate);

  // Loaded on MOUNT, not only on open — deciding whether to draw the button at all needs this
  // before anybody has clicked it, unlike every other use of `reference` here, which only
  // matters once the dialog is already open. Skipped entirely for anybody the cheap checks
  // above already exclude, so this never fires an extra request on a page load for the common
  // case of a requester with no create permission or the feature turned off outright. Default
  // true (shown) while it is still loading: this is a rendering courtesy an administrator can
  // turn off (`AiAssistantIdentity.ShowInHeader`), never an authorization boundary, so a brief
  // flash before it disappears costs nobody anything the wizard would not already refuse.
  useEffect(() => {
    if (!eligibleForFeatureAndPermission) return;

    getReference()
      .then(setReference)
      .catch(() => setReferenceError(true));
  }, [eligibleForFeatureAndPermission]);

  const eligible = eligibleForFeatureAndPermission && (reference?.assistantShowInHeader ?? true);

  const [phase, setPhase] = useState<Phase>("chat");
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<{ number: string; displayNumber: number } | null>(null);

  const [contactPhone, setContactPhone] = useState("");
  const [contactExtension, setContactExtension] = useState("");
  const [siteId, setSiteId] = useState("");
  const [gateError, setGateError] = useState<string | null>(null);

  // Regenerated every open, not every render — a retry within one open must reuse the same
  // idempotency key (the server returns the original create rather than a duplicate), so
  // this is a ref rather than state: it is read only inside the async create handler, never
  // during render.
  const idempotencyKey = useRef(crypto.randomUUID());

  // A separate value, in state rather than a ref, used ONLY as SupportAgentChat's `key` —
  // remounting it on every open gives it a clean conversation each time. Reading a ref's
  // `.current` during render (as the key prop would) is what a ref is not for; this is.
  const [chatKey, setChatKey] = useState(() => crypto.randomUUID());

  useEffect(() => {
    if (!open) return;

    idempotencyKey.current = crypto.randomUUID();
    setChatKey(crypto.randomUUID());
    setCreated(null);
    setGateError(null);

    const knownPhone = Boolean(user?.contact.contactPhone);
    const knownSite = Boolean(user?.siteId);

    setContactPhone(user?.contact.contactPhone ? formatPhone(user.contact.contactPhone) : "");
    setContactExtension(user?.contact.contactPhoneExtension ?? "");
    setSiteId(user?.siteId ?? "");
    setPhase(knownPhone && knownSite ? "chat" : "gate");

    if (!reference) {
      getReference()
        .then(setReference)
        .catch(() => setReferenceError(true));
    }
    // Deliberately only on open — re-running this while the person is mid-conversation
    // would wipe out what they had already typed into the gate or said to TARS.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function submitGate() {
    if (!normalisePhone(contactPhone)) {
      setGateError("Enter a number we can actually ring back — area code included.");
      return;
    }
    if (!siteId) {
      setGateError("Choose where you are.");
      return;
    }

    setGateError(null);
    setPhase("chat");
  }

  /** The team and the real category record behind a name TARS matched, if any. */
  function resolve(categoryId: string | null) {
    if (!categoryId || !reference) return null;

    for (const team of reference.teams) {
      const category = team.categories.find((c) => c.id === categoryId);
      if (category) return { team, category };
    }

    return null;
  }

  /**
   * Hands an unfinished conversation to the ordinary wizard, pre-filled — used for "Just
   * create a ticket", for a problem TARS could not place in a category, and for a create
   * that failed outright. The conversation itself is never lost to a server hiccup; it just
   * arrives on the form that would have asked for all of this anyway.
   */
  function handOff(handoff: SupportAgentHandoff) {
    const found = resolve(handoff.categoryId);

    writeRequestDraft({
      teamId: found?.team.id ?? "",
      categoryId: found?.category.id ?? "",
      subject: handoff.subject.slice(0, 200),
      description: handoff.description.slice(0, 4000),
      siteId,
      contactPhone,
      contactExtension,
      forUser: null,
    });

    setOpen(false);
    void navigate({ to: "/new" });
  }

  async function handleResolved(handoff: SupportAgentHandoff) {
    const found = resolve(handoff.categoryId);

    // TARS never matched a category it was allowed to suggest — same as the wizard's own
    // "should not happen, but if it did" branch: there is nothing to create against yet.
    if (!found) {
      handOff(handoff);
      return;
    }

    setCreating(true);

    try {
      const ticket = await createTicket({
        categoryId: found.category.id,
        subject: handoff.subject.slice(0, 200),
        description: handoff.description.slice(0, 4000),
        siteId: siteId || undefined,
        idempotencyKey: idempotencyKey.current,
        contactPhone: contactPhone.trim(),
        contactPhoneExtension: contactExtension.trim() || undefined,
      });

      setCreated({ number: ticket.number, displayNumber: ticket.displayNumber });
      setPhase("created");

      // The server has just kept this number and site against a profile that had none —
      // re-read the session so the NEXT time this opens, the gate is skipped, exactly as
      // the wizard already does after its own first successful create.
      //
      // ⚠️ Quiet, or `RequireSession` blanks and remounts the whole routed tree — including
      // this launcher — on the loud reload, discarding `created`/`phase` just set above.
      if (!user?.contact.contactPhone || !user?.siteId) void refresh(true).catch(() => undefined);
    } catch {
      // The conversation is not lost — it lands on the manual form with everything already
      // typed, exactly as a failed "This solved my issue" does inside the wizard itself.
      handOff(handoff);
    } finally {
      setCreating(false);
    }
  }

  if (!eligible) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {/* Gradient + glow, the same treatment as the "New ticket" CTA beside it — deliberately
          not the neutral bordered-icon style every other header button (theme, notifications)
          uses, so this one reads as an invitation rather than blending into the toolbar. The
          hover animation is transform/filter only (scale + brightness on the button, a small
          rotate + scale on the sparkle) — no custom @keyframes, nothing that runs before a
          pointer is actually over it. */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Get help from the AI support agent"
        title="Get help"
        className="group inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-primary text-primary-foreground shadow-glow transition-all duration-300 hover:scale-105 hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
      >
        <Sparkles
          className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110"
          aria-hidden
        />
      </button>

      <DialogContent className="max-w-lg gap-0 p-0 sm:max-w-xl">
        <DialogTitle className="sr-only">Get help</DialogTitle>

        {phase === "gate" && (
          <div className="p-6">
            <h2 className="text-sm font-semibold">Before we start</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Two things the desk needs on every request — then straight into the chat.
            </p>

            <div className="mt-4 space-y-4">
              <div>
                <span className="text-sm font-medium">How can we reach you?</span>
                <div className="mt-1 flex flex-wrap items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <label htmlFor="launcher-phone" className="text-xs text-muted-foreground">
                      Phone number
                    </label>
                    <input
                      id="launcher-phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      placeholder="(561) 555-0142"
                      className="mt-1 h-10 w-full rounded-lg border border-input bg-background px-3 text-base focus:border-ring focus:outline-none md:text-sm"
                    />
                  </div>
                  <div className="w-28">
                    <label htmlFor="launcher-extension" className="text-xs text-muted-foreground">
                      Ext. <span className="text-muted-foreground/70">(optional)</span>
                    </label>
                    <input
                      id="launcher-extension"
                      inputMode="numeric"
                      value={contactExtension}
                      onChange={(e) => setContactExtension(e.target.value)}
                      placeholder="4021"
                      className="mt-1 h-10 w-full rounded-lg border border-input bg-background px-3 text-base focus:border-ring focus:outline-none md:text-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="launcher-site" className="text-sm font-medium">
                  Where are you?
                </label>
                {reference ? (
                  <div className="mt-1">
                    <SiteCombobox
                      id="launcher-site"
                      sites={reference.sites}
                      value={siteId}
                      onChange={setSiteId}
                      required
                    />
                  </div>
                ) : referenceError ? (
                  <p className="mt-1.5 text-xs text-destructive">
                    Could not load the site list. Try closing and reopening this.
                  </p>
                ) : (
                  <div className="mt-1 flex h-10 items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                    Loading sites…
                  </div>
                )}
              </div>

              {gateError && (
                <p role="alert" className="text-xs text-destructive">
                  {gateError}
                </p>
              )}

              <button
                type="button"
                onClick={submitGate}
                disabled={!reference}
                className="inline-flex h-10 w-full items-center justify-center rounded-lg bg-primary text-sm font-semibold text-primary-foreground shadow-sm transition hover:opacity-90 disabled:opacity-50"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {phase === "chat" && (
          <div className="p-3 sm:p-4">
            <SupportAgentChat
              key={chatKey}
              name={reference?.assistantName}
              greeting={reference?.assistantGreeting}
              categoryId={null}
              creating={creating}
              // The top bar is the one entry point where the assistant IS the offer — "write it
              // myself" here just means closing this dialog and pressing "New ticket" instead,
              // so the link would be a second way to say something the launcher already lets
              // somebody do without opening this chat at all.
              showSkip={false}
              onResolved={(handoff) => void handleResolved(handoff)}
              onEscalate={(handoff) => handOff(handoff)}
              onSkip={() => {
                setOpen(false);
                void navigate({ to: "/new" });
              }}
              // A KB article solved it — no ticket to review, nothing left to hand off to.
              // The chat already shows its own "glad that helped" confirmation; this just
              // closes the dialog a beat later so it isn't yanked away mid-read.
              onDeflected={() => {
                window.setTimeout(() => setOpen(false), 1500);
              }}
            />
          </div>
        )}

        {phase === "created" && created && (
          <div className="flex flex-col items-center gap-3 p-8 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-success/15 text-success">
              <CheckCircle2 className="h-6 w-6" aria-hidden />
            </span>
            <div>
              <p className="text-sm font-semibold">Request #{created.displayNumber} created</p>
              <p className="mt-1 text-sm text-muted-foreground">
                We'll be in touch if we need anything else.
              </p>
            </div>
            <div className="mt-2 flex w-full gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex h-10 flex-1 items-center justify-center rounded-lg border border-input text-sm font-medium transition hover:bg-accent"
              >
                Done
              </button>
              <Link
                to="/tickets/$id"
                params={{ id: created.number }}
                onClick={() => setOpen(false)}
                className="inline-flex h-10 flex-1 items-center justify-center rounded-lg bg-primary text-sm font-semibold text-primary-foreground shadow-sm transition hover:opacity-90"
              >
                View request
              </Link>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
